import { useState, useMemo, useCallback, forwardRef } from 'react';
import { pick } from '../../utils/pick';
import { getDefaultPropName, getOnChangeName } from '../../utils/PropNames';
import { jsx as _jsx } from "react/jsx-runtime";
function getComponentName(Component, fallback = 'Component') {
  if (!Component) {
    return fallback;
  }
  return Component.displayName || Component.name || Component.type && (Component.type.displayName || Component.type.name) || fallback;
}
const DEFAULT_FIELDS = ['value'];
function getDefaultProps(component, fields) {
  const defaultProps = component.defaultProps || {};
  return fields.reduce((defaults, field) => {
    if (Object.prototype.hasOwnProperty.call(defaultProps, field)) {
      defaults[getDefaultPropName(field)] = defaultProps[field];
    }
    return defaults;
  }, {});
}
const PREV_PROP_PREFIX = 'prev_';
const toPrevPropKey = field => `${PREV_PROP_PREFIX}${field}`;
export function Controllable(InputComponent, fields = DEFAULT_FIELDS) {
  const onChangeKeys = fields.map(getOnChangeName);
  const defaultPropsFromInput = getDefaultProps(InputComponent, fields);
  const ControlledComponent = /*#__PURE__*/forwardRef((props, ref) => {
    const mergedProps = useMemo(() => {
      return Object.assign({}, defaultPropsFromInput, props);
    }, [props]);
    const defaults = pick(mergedProps, fields.map(getDefaultPropName));

    // Track if field is controlled
    const isControlled = fields.reduce((acc, field) => {
      acc[field] = mergedProps[field] !== undefined;
      return acc;
    }, {});
    const [state, setState] = useState(() => {
      const initialState = {};
      fields.forEach(field => {
        // Only set internal state for uncontrolled fields
        if (!isControlled[field]) {
          initialState[field] = defaults[getDefaultPropName(field)];
        }
        initialState[toPrevPropKey(field)] = mergedProps[field];
      });
      return initialState;
    });
    const handleChange = useCallback((field, evt) => {
      const value = evt.target.value;

      // Only update internal state if uncontrolled
      if (!isControlled[field]) {
        setState(prevState => Object.assign({}, prevState, {
          [field]: value
        }));
      }
      const onChange = mergedProps[getOnChangeName(field)];
      if (typeof onChange === 'function') {
        onChange(evt);
      }
    }, [mergedProps, isControlled]);
    const handlers = {};
    onChangeKeys.forEach((onChangeKey, i) => {
      handlers[onChangeKey] = evt => handleChange(fields[i], evt);
    });
    const getProps = () => {
      const applicableProps = {};
      const applicableState = {};
      fields.forEach(field => {
        // Use controlled value or internal state
        applicableState[field] = isControlled[field] ? mergedProps[field] : state[field];
      });
      Object.keys(mergedProps).forEach(key => {
        if (fields.map(getDefaultPropName).indexOf(key) === -1 && mergedProps[key] !== undefined) {
          applicableProps[key] = mergedProps[key];
        }
      });
      return Object.assign({}, applicableState, applicableProps, handlers);
    };
    const componentProps = Object.assign({}, getProps(), {
      ref
    });
    return /*#__PURE__*/_jsx(InputComponent, Object.assign({}, componentProps));
  });
  ControlledComponent.displayName = `Controllable(${getComponentName(InputComponent)})`;
  return ControlledComponent;
}